/**
 * AtlasLoadingModule — the boot loading screen.
 *
 * The #load-screen overlay in index.html is visible from FIRST PAINT (no JS
 * required), so the player never sees a half-streamed world. This module polls
 * the world systems' readiness getters and dismisses the overlay only when
 * terrain, water, sky, foliage, and structures are all in:
 *
 *   world      — connected + entered a world (atlas:world-entered)
 *   terrain    — the chunk streamer's initial window fully built, no pending jobs
 *   water      — the Commons sea surface built
 *   sky        — engine sky init + the night photo reached a terminal state
 *   foliage    — terrain done and no chunk scatter passes in flight
 *   structures — every settlement within render range assembled + the admin
 *                placement cache rendered
 *
 * While the overlay is up, `atlas.loadingGate` keeps AtlasAvatarModule from
 * consuming movement intent — the player cannot explore a world that isn't
 * there yet. Non-Commons first spawns (personal worlds) skip the Commons-only
 * checks: their terrain builds synchronously in EnvironmentModule.refresh.
 *
 * Failsafes (never trap the player behind a broken loader):
 *   - "Enter anyway" appears after SKIP_AFTER_MS,
 *   - a hard timeout dismisses at TIMEOUT_MS with an atlas:status note.
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { PLACES } from '../lib/loading_places.js';
import { bus } from '../bus.js';

const POLL_MS = 300;
// All durations are counted in POLLS, not wall-clock: performance.now() proved
// skewed by ~2 min in the headless shell (the 75 s failsafe fired at t≈12 s),
// and a setInterval poll count under-counts on a busy main thread — which only
// DELAYS the failsafe on slow machines, never fires it early.
/** Consecutive all-ready polls before dismissing (mid-churn guard). */
const STABLE_POLLS = 3;              // ≈ 900 ms
const MIN_SHOW_POLLS = 5;            // ≈ 1.5 s — a fast load shouldn't flash
const SKIP_AFTER_POLLS = 67;         // ≈ 20 s
// Timeouts are PROGRESS-AWARE (2026-08-15): a fixed 150 s budget stamped
// "timeout" on honest city assemblies (~120 s on a warm desktop, longer on a
// Quest over LAN) — the bar then lied at 100% with no buildings ("the
// buildings do not load in"). A stall timeout fires only after ≈75 s with
// ZERO forward progress; an absolute cap (≈5 min) still guarantees escape.
// The Enter-anyway skip stays at 20 s for anyone who'd rather take the pop-in.
const STALL_POLLS = 250;             // ≈ 75 s without any progress
const HARD_TIMEOUT_POLLS = 1000;     // ≈ 5 min absolute

type CheckKey = 'world' | 'terrain' | 'water' | 'sky' | 'foliage' | 'structures';

/** Structural readiness surface (the real modules satisfy this; the loading
 *  harness drives scripted stubs through the same interface). */
export interface LoadingDeps {
  environment: { loadReadiness(): { chunksBuilt: number; chunksTarget: number; chunksPending: number; foliagePending: number } };
  sky: { readonly ready: boolean };
  water: { readonly ready: boolean };
  settlements: { loadReadiness(): { resolved: boolean; inRange: number; built: number; assembling: number; frac: number } };
  adminEditor: { placementsReadiness(): { subscribed: boolean; cachedForWorld: number; rendered: number } };
}

export class AtlasLoadingModule implements IModule {
  readonly name = 'AtlasLoading';

  private worldEntered = false;
  private timer: number | null = null;
  private polls = 0;
  private allStreak = 0;
  private finished = false;
  private lastSig = '';
  private lastProgressPoll = 0;
  // ── Second loader: world→world transitions (not boot) ──
  private tOverlay: HTMLElement | null = null;
  private tTimer: number | null = null;
  private tPolls = 0;
  private tEntered = false;
  private tFromSpace = false;
  private tTargetId: bigint | null = null;

  constructor(private readonly deps: LoadingDeps, private readonly pollMs: number = POLL_MS) {}

  private app: App | null = null;

  init(_app: App): void {
    this.app = _app;
    const screen = document.getElementById('load-screen');
    if (!screen) {
      // Harness / embedded contexts ship no overlay — never gate exploration.
      atlas.loadingGate = false;
      return;
    }
    bus.on('atlas:world-entered', ({ worldId }) => {
      this.worldEntered = true;
      if (this.tTimer !== null) { this.tEntered = true; this.tTargetId = worldId; }
    });
    bus.on('atlas:world-swapping', ({ fromSpace }) => this.beginTransition(fromSpace));
    document.getElementById('load-skip')?.addEventListener('click', () => this.finish('skipped'));
    // Main menu: the poll clock (and its failsafe timeouts) must not run while
    // the player sits in the menu — begin on their Enter instead.
    if (document.getElementById('main-menu')) {
      bus.on('atlas:menu-enter', () => this.begin());
    } else {
      this.begin();
    }
  }

  private begin(): void {
    if (this.timer !== null || this.finished) return;
    this.timer = window.setInterval(() => this.poll(), this.pollMs);
  }

  /** One readiness pass: evaluate every check, paint the overlay, maybe finish. */
  private poll(): void {
    if (this.finished) return;
    this.polls++;
    const onCommons = atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId;

    const terrain = this.deps.environment.loadReadiness();
    const settle = this.deps.settlements.loadReadiness();
    const adm = this.deps.adminEditor.placementsReadiness();

    const terrainDone = onCommons
      ? terrain.chunksTarget > 0 && terrain.chunksBuilt >= terrain.chunksTarget && terrain.chunksPending === 0
      : this.worldEntered; // personal worlds: the heightfield builds synchronously in refresh()
    // Admin placements: until the initial subscription sync lands the cache is
    // empty (0 ≥ 0 would false-positive) — require rows unless none ever arrive.
    const admDone = adm.subscribed && (adm.cachedForWorld > 0 ? adm.rendered >= adm.cachedForWorld : this.polls > 40);
    const checks: Record<CheckKey, boolean> = {
      world: this.worldEntered,
      terrain: terrainDone,
      water: onCommons ? this.deps.water.ready : this.worldEntered,
      sky: this.deps.sky.ready,
      foliage: onCommons ? terrainDone && terrain.foliagePending === 0 : this.worldEntered,
      structures: onCommons
        // resolved is load-bearing: before the Commons seed lands the counts
        // are all zero and 0 >= 0 read as "done" — the gate cleared while the
        // spawn city was still assembling (bar at 100 %, no buildings).
        ? settle.resolved && settle.built >= settle.inRange && settle.assembling === 0 && (settle.inRange > 0 || terrainDone) && admDone
        : this.worldEntered,
    };

    // ── Paint ──
    const list = document.getElementById('load-checks');
    if (list) {
      for (const li of Array.from(list.querySelectorAll('li'))) {
        const k = li.dataset.check as CheckKey;
        li.dataset.state = checks[k] ? 'done' : this.worldEntered || k === 'world' ? 'active' : 'pending';
        const detail = li.querySelector('.load-detail');
        if (detail) {
          detail.textContent =
            k === 'terrain' && onCommons && terrain.chunksTarget > 0 && !checks.terrain
              ? `${terrain.chunksBuilt}/${terrain.chunksTarget}`
              : k === 'structures' && onCommons && settle.inRange > 0 && !checks.structures
                ? `${settle.built}/${settle.inRange}`
                : '';
        }
      }
    }
    const terrainFrac = terrain.chunksTarget > 0 ? Math.min(1, terrain.chunksBuilt / terrain.chunksTarget) : 0;
    // frac carries mid-assembly stage progress (GLB loads) — the bar moves
    // during the long city build instead of sitting at built-sites-only.
    const structFrac = settle.resolved && settle.inRange > 0 ? Math.min(1, settle.frac) : checks.structures ? 1 : 0;
    const frac =
      ((checks.world ? 1 : 0) +
        (checks.terrain ? 1 : onCommons ? terrainFrac : 0) +
        (checks.water ? 1 : 0) +
        (checks.sky ? 1 : 0) +
        (checks.foliage ? 1 : 0) +
        (checks.structures ? 1 : structFrac)) / 6;
    const fill = document.getElementById('load-bar-fill');
    if (fill) fill.style.width = `${Math.round(frac * 100)}%`;
    const sub = document.getElementById('load-sub');
    if (sub && this.worldEntered) sub.textContent = onCommons ? 'Preparing the Commons…' : 'Preparing your world…';

    // ── Failsafes ──
    const skip = document.getElementById('load-skip');
    if (skip && this.polls > SKIP_AFTER_POLLS) skip.style.display = '';
    // Progress-aware timeout: any advancing signal (a check flipping, chunks
    // built, structure stage fractions) refreshes the stall budget.
    const sig = `${Object.values(checks).map((c) => (c ? 1 : 0)).join('')}|${terrain.chunksBuilt}|${settle.built}|${Math.round(settle.frac * 50)}`;
    if (sig !== this.lastSig) { this.lastSig = sig; this.lastProgressPoll = this.polls; }
    if (this.polls - this.lastProgressPoll > STALL_POLLS || this.polls > HARD_TIMEOUT_POLLS) { this.finish('timeout'); return; }

    // ── Done? (all checks stable + minimum show time) ──
    const all = Object.values(checks).every(Boolean);
    if (!all) { this.allStreak = 0; return; }
    this.allStreak++;
    if (this.allStreak >= STABLE_POLLS && this.polls >= MIN_SHOW_POLLS) this.finish('complete');
  }

  private finish(reason: 'complete' | 'skipped' | 'timeout'): void {
    if (this.finished) return;
    this.finished = true;
    console.info(`[loading] finished (${reason}) after ${this.polls} polls`);
    if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
    atlas.loadingGate = false;
    bus.emit('atlas:loading-done', {});
    const screen = document.getElementById('load-screen');
    if (screen) {
      screen.classList.add('gone');
      window.setTimeout(() => screen.remove(), 700);
    }
    if (reason !== 'complete') {
      bus.emit('atlas:status', { text: 'entered while the world is still streaming — pop-in may occur nearby' });
    }
  }

  // ── The SECOND loading screen: shown for a mid-game world swap. Holds the
  //    avatar (loadingGate) while the old area despawns and the new world
  //    streams in behind the cover, then reveals — the space→ground teardown
  //    no longer overlaps a live-rendered destination (the GPU flood path). ──
  private beginTransition(fromSpace: boolean): void {
    if (this.tTimer !== null) return; // already transitioning
    this.tFromSpace = fromSpace;
    this.tEntered = false;
    this.tTargetId = null;
    this.tPolls = 0;
    atlas.loadingGate = true; // AtlasAvatarModule parks the walker while we hold
    // HOLD ALL GPU SUBMITS while the old area tears down + the new world builds
    // behind the cover — no in-flight command buffer can reference a resource
    // three disposes during the swap ("used in submit while destroyed"). The
    // DOM overlay stays visible; systems keep ticking, only the draw is skipped.
    try { if (this.app) (this.app.renderer as unknown as { paused: boolean }).paused = true; (globalThis as { __holdDisposal?: boolean }).__holdDisposal = true; } catch { /* no renderer */ }
    this.tOverlay = this.buildTransitionOverlay();
    document.body.appendChild(this.tOverlay);
    this.tTimer = window.setInterval(() => this.transitionPoll(), this.pollMs);
  }

  private buildTransitionOverlay(): HTMLElement {
    const o = document.createElement('div');
    o.id = 'world-transition';
    // One of 20 real Atlas locations as the backdrop, with its fact.
    const place = PLACES[Math.floor(Math.random() * PLACES.length)];
    o.style.cssText =
      'position:fixed;inset:0;z-index:200;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:20px;' +
      `background:var(--ax-bg) center/cover no-repeat;color:var(--ax-text);` +
      'font:14px var(--ax-font);opacity:0;transition:opacity .35s;pointer-events:auto';
    // Preload the image so it never flashes empty; fall back to the gradient.
    const bg = new Image();
    bg.onload = () => { o.style.backgroundImage = `linear-gradient(rgba(3,6,11,.35),rgba(3,6,11,.72)), url('${place.img}')`; };
    bg.src = place.img;
    const ring = document.createElement('div');
    ring.style.cssText =
      'width:54px;height:54px;border-radius:50%;border:3px solid var(--ax-line);border-top-color:var(--ax-accent);' +
      'animation:wt-spin 0.9s linear infinite';
    const title = document.createElement('div');
    title.textContent = this.tFromSpace ? 'Leaving orbit…' : 'Traveling…';
    title.style.cssText = 'font:700 18px var(--ax-font);color:var(--ax-text);letter-spacing:.5px';
    const subEl = document.createElement('div');
    subEl.id = 'wt-sub';
    subEl.textContent = this.tFromSpace ? 'Powering down the ship and despawning the sector' : 'Despawning the current world';
    subEl.style.cssText = 'color:var(--ax-soft)';
    const bar = document.createElement('div');
    bar.style.cssText = 'width:280px;height:4px;border-radius:var(--ax-radius);background:var(--ax-bg3);overflow:hidden';
    const fill = document.createElement('div');
    fill.id = 'wt-fill';
    fill.style.cssText = 'height:100%;width:8%;background:linear-gradient(90deg,var(--ax-accent),var(--ax-good));transition:width .3s';
    bar.appendChild(fill);
    if (!document.getElementById('wt-style')) {
      const st = document.createElement('style');
      st.id = 'wt-style';
      st.textContent = '@keyframes wt-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(st);
    }
    // Location plate (name + fact) pinned to the lower third.
    const plate = document.createElement('div');
    plate.style.cssText = 'position:absolute;left:0;right:0;bottom:8%;display:flex;flex-direction:column;align-items:center;gap:6px;padding:0 8%;text-align:center';
    const pname = document.createElement('div');
    pname.textContent = place.name;
    pname.style.cssText = 'font:700 22px var(--ax-font);color:var(--ax-text);text-shadow:0 2px 12px rgba(0,0,0,.8);letter-spacing:.5px';
    const pfact = document.createElement('div');
    pfact.textContent = place.fact;
    pfact.style.cssText = 'max-width:640px;font:15px/1.5 var(--ax-font);color:var(--ax-text);text-shadow:0 1px 8px rgba(0,0,0,.9)';
    plate.append(pname, pfact);
    o.append(ring, title, subEl, bar, plate);
    requestAnimationFrame(() => { o.style.opacity = '1'; });
    return o;
  }

  private transitionPoll(): void {
    this.tPolls++;
    const onCommons = this.tTargetId !== null && atlas.commonsWorldId !== null && this.tTargetId === atlas.commonsWorldId;
    // Readiness: world entered + terrain settled (Commons streams; personal
    // worlds build synchronously in refresh) + a minimum hold so the old
    // area's deferred GPU disposals flush behind the cover.
    let terrainReady = this.tEntered;
    try {
      if (onCommons) {
        const t = this.deps.environment.loadReadiness();
        terrainReady = this.tEntered && t.chunksTarget > 0 && t.chunksBuilt >= t.chunksTarget && t.chunksPending === 0;
      }
    } catch { /* deps mid-churn */ }
    const minHold = this.tPolls >= (this.tFromSpace ? 16 : 6); // fromSpace: ~3.2 s so the sector's deferred disposals fully flush behind the cover
    const ready = this.tEntered && terrainReady && minHold;
    const failsafe = this.tPolls > 90;                // ~18 s hard cap
    const sub = document.getElementById('wt-sub');
    if (sub) sub.textContent = !this.tEntered ? (this.tFromSpace ? 'Despawning the sector…' : 'Despawning the world…') : terrainReady ? 'Arriving…' : 'Building the world…';
    const fill = document.getElementById('wt-fill');
    if (fill) fill.style.width = `${Math.min(96, 8 + this.tPolls * (onCommons ? 6 : 16))}%`;
    if (ready || failsafe) this.endTransition(failsafe && !ready ? 'timeout' : 'complete');
  }

  private endTransition(reason: 'complete' | 'timeout'): void {
    if (this.tTimer !== null) { window.clearInterval(this.tTimer); this.tTimer = null; }
    // Resume rendering: the destination world is built, so the first drawn
    // frame references only live resources. Drain the shield's backlog first.
    try { if (this.app) (this.app.renderer as unknown as { paused: boolean }).paused = false; (globalThis as { __holdDisposal?: boolean }).__holdDisposal = false; } catch { /* ok */ }
    atlas.loadingGate = false;
    bus.emit('atlas:loading-done', {});
    console.info(`[transition] ${reason} after ${this.tPolls} polls (fromSpace=${this.tFromSpace})`);
    const o = this.tOverlay;
    this.tOverlay = null;
    if (o) { o.style.opacity = '0'; window.setTimeout(() => o.remove(), 400); }
  }

  dispose(): void {
    if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
    if (this.tTimer !== null) { window.clearInterval(this.tTimer); this.tTimer = null; }
    this.tOverlay?.remove();
  }
}
